import { CHALLENGE_ROSTER, challengeLevelForBoss } from '../data/challengeBosses.js';
import { isBossLocked } from '../data/acts.js';
import { audio } from '../systems/audio/AudioSystem.js';
import { BOSS_SPRITES } from '../data/sprites.js';
import { drawSprite } from '../core/Sprite.js';

// Pixel portrait for a boss card — same approach as CharacterSelect's
// buildPortrait, just reading from BOSS_SPRITES instead of PLAYER_SPRITES.
function buildPortrait(bossDef) {
  const sprite = BOSS_SPRITES[bossDef.spriteKey];
  const canvas = document.createElement('canvas');
  canvas.className = 'character-portrait';
  const w = Math.ceil((sprite.rows[0]?.length ?? 0) * sprite.pixelSize);
  const h = Math.ceil(sprite.rows.length * sprite.pixelSize);
  const size = Math.max(w, h) + 2;
  canvas.width = size;
  canvas.height = size;
  drawSprite(canvas.getContext('2d'), sprite, size / 2, size / 2);
  return canvas;
}

// Gauntlet bosses are labeled by their tier position; the true final boss
// is flagged on its def, so the label survives roster growth (the 2026-07
// expansion took the gauntlet from four bosses to seven). Exclusives carry
// their own `subtitle` and never reach this.
const TIER_NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

function tierLabel(bossDef, index) {
  if (bossDef.isFinalBoss) return 'Final Boss';
  return `Tier ${TIER_NUMERALS[index] ?? index + 1}`;
}

/**
 * Boss Challenge pick screen — a practice mode that drops the player
 * straight into a single chosen boss fight (no wave-defense stage, no
 * herald gauntlet even for The Unmade King). Modeled directly on
 * CharacterSelect.js's card-grid pattern; picking a card hands the chosen
 * roster index up to main.js, which routes to CharacterSelect next (a
 * champion is still required to build a Game) before actually starting.
 *
 * Lists CHALLENGE_ROSTER: the gauntlet bosses first (in gauntlet order),
 * then the challenge-exclusive bosses (data/challengeBosses.js) — the only
 * place in the game the exclusives are reachable from.
 *
 * Act II and Act III cards are held behind the same bar act select and the
 * codex use (acts.js isBossLocked). This screen used to name every boss in the
 * game, portrait and all, to a save that had cleared nothing, and let it pick
 * The Anthem — which made it the one screen that undid the spoiler gating the
 * others are careful about. A locked card stays in place, so the roster's shape
 * is still honest about there being more; it just doesn't say what.
 */
export class BossSelect {
  constructor(root, save, { onPick, onBack }) {
    this.root = root;
    this.save = save;
    this.onPick = onPick;
    this.onBack = onBack;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay character-select-overlay boss-select-overlay';
    this._render();
    this.root.appendChild(this.el);
  }

  _render() {
    this.el.innerHTML = '';

    const header = document.createElement('header');
    header.className = 'character-select-header';
    header.innerHTML = '<div class="menu-title">BOSS CHALLENGE</div><div class="menu-subtitle">Pick a boss to fight directly — no waves, no rewards, just the fight.</div>';
    this.el.appendChild(header);

    const cards = document.createElement('div');
    cards.className = 'character-cards boss-select-cards';
    CHALLENGE_ROSTER.forEach((bossDef, index) => cards.appendChild(this._buildCard(bossDef, index)));
    this.el.appendChild(cards);

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  _buildCard(bossDef, index) {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'levelup-card';
    card.style.setProperty('--champ', bossDef.arenaTheme.accent);

    // Locked: no portrait, no name, no act attribution, no start level — the
    // same deliberately contentless treatment ActSelect gives a locked act,
    // for the same reason. Everything a spoiler could ride on is withheld,
    // including the silhouette.
    if (isBossLocked(bossDef, this.save?.statistics)) {
      card.classList.add('levelup-card-locked');
      card.disabled = true;
      const label = document.createElement('div');
      label.className = 'levelup-card-label';
      label.textContent = '🔒 ???';
      card.appendChild(label);
      const desc = document.createElement('div');
      desc.className = 'levelup-card-desc';
      desc.textContent = 'Locked — finish the act before this one to unlock it.';
      card.appendChild(desc);
      return card;
    }

    card.appendChild(buildPortrait(bossDef));

    const label = document.createElement('div');
    label.className = 'levelup-card-label';
    label.textContent = bossDef.name;
    card.appendChild(label);

    const desc = document.createElement('div');
    desc.className = 'levelup-card-desc';
    desc.textContent = `${bossDef.subtitle ?? tierLabel(bossDef, index)} · Starts at level ${challengeLevelForBoss(bossDef)}`;
    card.appendChild(desc);

    card.addEventListener('click', () => {
      audio.playSfx('click');
      this.onPick(index);
    });
    return card;
  }

  destroy() {
    this.el.remove();
  }
}
